import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
// import Modalform from './Modalform';
// import HomeCommonSec from './HomeCommonSec';

const API_BASE = "https://phpstack-1434504-6130285.cloudwaysapps.com";
const API_URL =
  "https://phpstack-1434504-6130285.cloudwaysapps.com/api/popular-blog?populate[popularblog][populate][master_blogs][populate][image]=true&populate[popularblog][populate][master_blogs][populate][og_image]=true&publicationState=live";


// --------------- helpers ---------------
const abs = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return `${API_BASE}/${u}`;
};

const getAttr = (node) => node?.data?.attributes || node?.attributes || node || null;

const pickFormat = (formats) => {
  if (!formats || typeof formats !== "object") return null;
  return (
    formats.medium?.url ||
    formats.large?.url ||
    formats.small?.url ||
    formats.thumbnail?.url ||
    null
  );
};

// Works for og_image / image (single, array, with formats, direct url)
const getBestImageUrl = (imageNode) => {
  if (!imageNode) return "";
  if (Array.isArray(imageNode?.data)) {
    for (const it of imageNode.data) {
      const a = getAttr(it);
      const byFormat = pickFormat(a?.formats);
      if (byFormat) return abs(byFormat);
      if (a?.url) return abs(a.url);
    }
  }
  const attr = getAttr(imageNode);
  if (attr) {
    const byFormat = pickFormat(attr.formats);
    if (byFormat) return abs(byFormat);
    if (attr.url) return abs(attr.url);
  }
  if (typeof imageNode?.url === "string") return abs(imageNode.url);
  return "";
};

const toPlainText = (rich) => {
  if (!rich) return "";
  if (typeof rich === "string") return rich;
  if (Array.isArray(rich)) {
    return rich
      .map((blk) =>
        Array.isArray(blk?.children)
          ? blk.children.map((c) => c?.text || "").join(" ")
          : ""
      )
      .join(" ");
  }
  if (typeof rich === "object") {
    if (Array.isArray(rich?.children)) {
      return rich.children.map((c) => c?.text || "").join(" ");
    }
    return Object.values(rich).join(" ");
  }
  return "";
};

const trimWords = (str, n = 30) => {
  if (typeof window === "undefined") return str || "";
  const div = document.createElement("div");
  div.innerHTML = str || "";
  const text = div.textContent || div.innerText || "";
  const parts = text.trim().split(/\s+/);
  if (parts.length <= n) return text;
  return parts.slice(0, n).join(" ") + "…";
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "long", day: "2-digit", year: "numeric" });
};

const slugToTitle = (slug) =>
  (slug || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function Home() {
  // ✅ Hooks must be inside the component
  const routerLocation = useLocation();
  const pathParts = routerLocation.pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);

  // ✅ Fallback to default "india" if no location found in path
  const countrySlug = pathParts[2] || "india";

  // ✅ Get dynamic param if your route is like "/:location/..."
  const { location: locationParam } = useParams(); // Keep original structure

  // Effective location to use everywhere (param → slug → default)
  const effectiveLocation = locationParam || countrySlug || "india";
  const locationName = slugToTitle(effectiveLocation); // For human-readable titles

  // Modal state (left as-is)
  const [showModal, setShowModal] = useState(false);
  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // API state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ✅ SEO: update title and description when effectiveLocation changes
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Picano India | School Furniture Manufacturer & Supplier ${locationName}`;

    const metaDescriptionTag = document.querySelector('meta[name="description"]');
    const contentText = `Picano India is a trusted school furniture manufacturer and supplier in  ${locationName}, offering durable desks, chairs, and classroom furniture solutions.`;
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute("content", contentText);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.name = "description";
      newMetaTag.content = `Picano India is a trusted school furniture manufacturer and supplier in  ${locationName}, offering durable desks, chairs, and classroom furniture solutions.`;
      document.head.appendChild(newMetaTag);
    }

    return () => {
      document.title = prevTitle || "Picano";
    };
  }, [locationName]);

  // Fetch popularblog
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await fetch(API_URL, { headers: { "Content-Type": "application/json" } });
        const text = await res.text();
        if (!res.ok) throw new Error(`API ${res.status}: ${text || res.statusText}`);
        const json = text ? JSON.parse(text) : {};
        // structure: { data: { popularblog: { master_blogs: [] } } }
        const raw =
          json?.data?.popularblog?.master_blogs ||
          json?.data?.attributes?.popularblog?.master_blogs ||
          [];

        const normalized = raw.map((item, idx) => {
          const id = item?.id ?? idx;
          const title = (item?.title || item?.seo_title || "Untitled")
            // Replace any "{locationName}" placeholder with the readable name
            .replace(/{locationName}/gi, locationName);
          const slug = item?.slug || `blog-${idx}`;
          const imgUrl = getBestImageUrl(item?.og_image) || getBestImageUrl(item?.image);
        const shortdis = trimWords(
  toPlainText(
    (item?.short_description || item?.description || "")
      .replace(/{locationName}/gi, locationName)
  ),
  18
);
          const date = item?.publishedAt || item?.createdAt || null;
          return { id, title, slug, imgUrl, shortdis, date };
        }); 

        if (!cancelled) setItems(normalized);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load blogs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationName]);

  // Sidebar (Popular Articles) — first 3
  const sidebarTop3 = useMemo(() => items.slice(0, 3), [items]);

  return (
    <div>
      <div id="colorlib-main">
        <section className="ftco-section ftco-no-pt ftco-no-pb">
          <div className="container">
            <div className="row d-flex">
              {/* LEFT: list */}
              <div className="col-xl-8 py-5 px-md-5">
                <div className="row pt-md-4">
                  {loading && (
                    <div className="col-12 py-5 text-center">Loading…</div>
                  )}
                  {err && (
                    <div className="col-12">
                      <div className="alert alert-danger">{err}</div>
                    </div>
                  )}
                  {!loading && !err && items.length === 0 && (
                    <div className="col-12 text-center py-5">No posts found.</div>
                  )}

                  {!loading && !err && items.length > 0 && items.map((b) => (
                    <div className="col-md-12" key={b.id}>
                      <div className="blog-entry d-md-flex">
                        <Link
                          to={`/blog/${effectiveLocation}/${b.slug}`}
                          className="img img-2"
                          style={{ backgroundImage: `url(${b.imgUrl || "img/placeholder.png"})` }}
                          aria-label={b.title}
                        />
                        <div className="text text-2 pl-md-4">
                          <h3 className="mb-2">
                            <Link to={`/blog/${effectiveLocation}/${b.slug}`}>{b.title}</Link>
                          </h3>

                          <div className="meta-wrap">
                            <p className="meta">
                              <span>
                                {b.date ? (
                                  <>
                                    <i className="ri-calendar-check-line  mr-2"></i>
                                    {formatDate(b.date)}
                                  </>
                                ) : null}
                              </span>
                              {/* Optional category / comments can go here if available */}
                            </p>
                          </div>

                          <p className="mb-4">{b.shortdis}</p>
                          <p>
                            <Link to={`/blog/${effectiveLocation}/${b.slug}`} className="btn-custom">
                              Read More <span className="ri-arrow-right-line"></span>
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* (Optional) pagination UI block tha — aapne "jitna ho sab dikhana" bola hai, isliye remove kiya.
                    Agar chahein to yahan real pagination add kar denge. */}
              </div>

              {/* RIGHT: Sidebar */}
              <div className="col-xl-4 sidebar bg-light pt-5 position-relative">
                <div className="sidebar-box pt-md-4">
                  <Link to={`/blog/${effectiveLocation}/search`}>
                    <form action="#" className="search-form" onSubmit={(e)=>e.preventDefault()}>
                      <div className="form-group">
                        <span className="icon ri-search-line"></span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Your Blog..."
                          readOnly
                        />
                      </div>
                    </form>
                  </Link>
                </div>

                <div className="sidebar-box">
                  <h3 className="sidebar-heading">Popular Articles</h3>

                  {/* Top 3 items from same API in sidebar cards */}
                  {sidebarTop3.map((b) => (
                    <div className="block-21 mb-4 d-flex" key={b.id}>
                      <Link
                        className="blog-img mr-4"
                        style={{ backgroundImage: `url(${b.imgUrl || "img/placeholder.png"})` }}
                        to={`/blog/${effectiveLocation}/${b.slug}`}
                        aria-label={b.title}
                      />
                      <div className="text">
                        <h3 className="heading">
                          <Link to={`/blog/${effectiveLocation}/${b.slug}`}>{b.title}</Link>
                        </h3>
                        <div className="meta">
                          {b.date && (
                            <div>
                              <Link to={`/blog/${effectiveLocation}/${b.slug}`}>
                                <span className="ri-calendar-check-line "></span> {formatDate(b.date)}
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {(!loading && !err && sidebarTop3.length === 0) && (
                    <div className="text-muted">No popular articles.</div>
                  )}
                </div>

                {/* <div className="sidebar-box">
                  <h3 className="sidebar-heading">Find Your Tag</h3>
                  <ul className="tagcloud">
                    <Link to="#" className="tag-cloud-link">IVF</Link>
                    <Link to="#" className="tag-cloud-link">firtilty</Link>
                    <Link to="#" className="tag-cloud-link">Orthopedics</Link>
                    <Link to="#" className="tag-cloud-link">spinal</Link>
                    <Link to="#" className="tag-cloud-link">hair</Link>
                    <Link to="#" className="tag-cloud-link">hip</Link>
                    <Link to="#" className="tag-cloud-link">knee</Link>
                    <Link to="#" className="tag-cloud-link">brain</Link>
                  </ul>
                </div> */}

                <div className="sidebar-box" style={{position:"sticky", top:"20px"}}>
                  <h3 className="sidebar-heading">About Us</h3>
                   <p className="text-lg mb-4">
                   Picano Interio LLP, established in 2003, stands as a prominent player in the realm of furniture manufacturing, particularly renowned for its excellence in crafting top-notch classroom furniture.🌍❤{" "}
                    <strong style={{ color: "rgb(198 87 160)" }}>
                      #Picanoindia #Furniture
                    </strong>
                  </p>

                  <h3 className="sidebar-heading">Follow Us</h3>
                 <div className="social-icons mt-3 d-flex flex-wrap gap-3" style={{ fontSize: "22px" }}>
                                    {/* social links (unchanged) */}
                                    <Link to="https://www.facebook.com/PicanoSchoolFurnitureManufacturer/" target="_blank" rel="noopener noreferrer"><i className="ri-facebook-fill"></i></Link>
                                    <Link to="https://www.instagram.com/picanoin/" target="_blank" rel="noopener noreferrer" title="Instagram India"><i className="ri-instagram-line"></i></Link>
                                    {/* <Link to="https://www.youtube.com/@MedCareTrust" target="_blank" rel="noopener noreferrer" title="YouTube India"><i className="ri-youtube-fill"></i></Link>
                                    <Link to="https://www.linkedin.com/company/medcaretrust" target="_blank" rel="noopener noreferrer" title="LinkedIn"><i className="ri-linkedin-fill"></i></Link>
                                    <Link to="https://x.com/medcaretrust" target="_blank" rel="noopener noreferrer" title="X (Twitter)"><i className="ri-twitter-fill"></i></Link>
                                    <Link to="https://t.me/MedCareTrust" target="_blank" rel="noopener noreferrer" title="Telegram Channel"><i className="ri-telegram-line"></i></Link>
                                    <Link to="https://chat.whatsapp.com/HNHGSzccqC0G3yEauXJrz9" target="_blank" rel="noopener noreferrer" title="WhatsApp Community"><i className="ri-whatsapp-line"></i></Link>
                                    <Link to="https://www.reddit.com/user/MedCareTrust/" target="_blank" rel="noopener noreferrer" title="Reddit"><i className="ri-reddit-line"></i></Link>
                                    <Link to="https://medium.com/@MedCareTrust" target="_blank" rel="noopener noreferrer" title="Medium"><i className="ri-medium-line"></i></Link>
                                    <Link to="https://open.spotify.com/show/2YRcfGsYA36ziRd14pIaln?si=eafbf9c317c74562" target="_blank" rel="noopener noreferrer" title="Spotify Podcast"><i className="ri-spotify-line"></i></Link>
                                    <Link to="http://wasap.my/919915447777" target="_blank" rel="noopener noreferrer" title="WhatsApp Direct"><i className="ri-whatsapp-line"></i></Link>
                                    <Link to="https://www.quora.com/profile/MedCareTrust" target="_blank" rel="noopener noreferrer" title="Quora"><i className="ri-quora-line">Q</i></Link>
                                    <Link to="https://www.instagram.com/medcaretrustbangladesh/" target="_blank" rel="noopener noreferrer" title="Instagram Bangladesh"><i className="ri-instagram-line"></i></Link>
                                    <Link to="https://www.youtube.com/@MedCareTrustBangladesh786" target="_blank" rel="noopener noreferrer" title="YouTube Bangladesh"><i className="ri-youtube-fill"></i></Link>
                                       <Link to="https://aratt.ai/@medcare_trust" target="_blank" rel="noopener noreferrer" title="aratt medcare"> <img
                          className="w-75 entered lazyloaded"
                          src={require("./images/AI.png")}
                          alt={`Medcare trust `} 
                        /></Link> */}
                                  </div>
                </div>
              </div>
              {/* END COL */}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Home;
